import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../utils/api';
import { TrendingUp, ShoppingBag, Users, Eye, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const STATUS_COLORS = { pending:'#D97706', processing:'#2563EB', shipped:'#7C3AED', delivered:'#16A34A', cancelled:'#DC2626', refunded:'#6B7280' };
const STATUS_ALB = { pending:'Në pritje', processing:'Duke u procesuar', shipped:'Dërguar', delivered:'Dorëzuar', cancelled:'Anuluar', refunded:'Rimbursuar' };
const CHART_COLOR = '#2E5C42';

export default function DashboardPage() {
  const [range, setRange] = useState('30');
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-dashboard', range],
    queryFn: () => analyticsAPI.dashboard({ range }).then(r => r.data),
    refetchInterval: 60000,
  });

  if (isLoading) return <div className="loading-screen" style={{ minHeight: 400 }}><div className="spinner" /></div>;

  const s = data?.summary || {};
  const growth = s.revenue_growth;

  return (
    <div className="dashboard">
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Dashboard</h1>
          <p style={{ color: 'var(--text-light)', fontSize: '.9rem' }}>Mirë se erdhe! Ja çfarë po ndodh sot.</p>
        </div>
        <div className="range-tabs">
          {[['7','7 ditë'],['30','30 ditë'],['90','90 ditë']].map(([v, l]) => (
            <button key={v} className={`range-tab${range === v ? ' active' : ''}`} onClick={() => setRange(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <KPICard icon={<TrendingUp />} label="Të Ardhura" value={`$${s.revenue?.toFixed(2)}`} 
          sub={`${s.order_count} porosi`} trend={growth} color="green" />
        <KPICard icon={<ShoppingBag />} label="Porosi" value={s.order_count} 
          sub={`Mesatare $${s.order_count ? (s.revenue/s.order_count).toFixed(0) : 0}/porosi`} color="blue" />
        <KPICard icon={<Users />} label="Klientë të Rinj" value={s.customers} sub="Klientë të regjistruar" color="gold" />
        <KPICard icon={<Eye />} label="Vizita" value={s.page_views?.toLocaleString()} sub="Shikime totale" color="purple" />
      </div>

      {/* Charts row */}
      <div className="charts-row">
        <div className="chart-card wide">
          <div className="chart-title">Të Ardhurat Ditore</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data?.revenueByDay || []} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B8070' }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#6B8070' }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={(v) => [`$${v}`, 'Të ardhura']} labelFormatter={l => `Datë: ${l}`} />
              <Line type="monotone" dataKey="revenue" stroke={CHART_COLOR} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <div className="chart-title">Statusi i Porosive</div>
          {data?.ordersByStatus?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.ordersByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={85} label={({ status, percent }) => `${STATUS_ALB[status] || status} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {data.ordersByStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || '#6B7280'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, STATUS_ALB[n] || n]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="no-data">Nuk ka porosi ende</div>}
        </div>
      </div>

      {/* Bottom row */}
      <div className="bottom-row">
        {/* Top products */}
        <div className="table-card">
          <div className="chart-title">Produktet Bestseller</div>
          {data?.topProducts?.length > 0 ? (
            <table><thead><tr><th>Produkti</th><th>Njësi</th><th>Të Ardhura</th></tr></thead>
              <tbody>
                {data.topProducts.map((p, i) => {
                  const img = (() => { try { return JSON.parse(p.images)?.[0]; } catch { return null; } })();
                  return (
                    <tr key={i}>
                      <td><div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        {img && <img src={img} alt="" style={{ width:36,height:36,objectFit:'contain',borderRadius:6,background:'var(--cream)' }} />}
                        <span style={{ fontWeight:500 }}>{p.name}</span>
                      </div></td>
                      <td>{p.units_sold}</td>
                      <td style={{ fontWeight:600,color:'var(--green-deep)' }}>${(+p.revenue).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <div className="no-data">Nuk ka të dhëna ende</div>}
        </div>

        {/* Low stock */}
        <div className="table-card">
          <div className="chart-title" style={{ display:'flex',alignItems:'center',gap:8 }}>
            <AlertTriangle size={16} color="var(--warning)" /> Stok i Ulët
          </div>
          {data?.lowStock?.length > 0 ? (
            <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
              {data.lowStock.map(p => (
                <div key={p.id} className="low-stock-row">
                  <span style={{ fontWeight:500,fontSize:'.88rem' }}>{p.name}</span>
                  <span className={`badge ${p.stock === 0 ? 'badge-red' : 'badge-gold'}`}>
                    {p.stock === 0 ? 'Jashtë stoku' : `${p.stock} mbetur`}
                  </span>
                </div>
              ))}
            </div>
          ) : <div className="no-data" style={{ color:'var(--success)',fontWeight:500 }}>✅ Të gjitha produktet kanë stok të mjaftueshëm</div>}
        </div>

        {/* Recent orders */}
        <div className="table-card">
          <div className="chart-title">Porositë e Fundit</div>
          {data?.recentOrders?.length > 0 ? (
            <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
              {data.recentOrders.map(o => (
                <div key={o.order_number} className="recent-order-row">
                  <div>
                    <div style={{ fontSize:'.82rem',fontWeight:600 }}>{o.order_number}</div>
                    <div style={{ fontSize:'.75rem',color:'var(--text-light)' }}>{o.user_name || 'Guest'}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontWeight:700,color:'var(--green-deep)' }}>${(+o.total).toFixed(2)}</div>
                    <span className={`badge badge-${o.status === 'delivered' ? 'green' : o.status === 'cancelled' ? 'red' : 'gold'}`}>
                      {STATUS_ALB[o.status]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="no-data">Nuk ka porosi ende</div>}
        </div>
      </div>

      <style>{`
        .dashboard { max-width:1100px; }
        .dash-header { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;flex-wrap:wrap;gap:16px; }
        .dash-title { font-size:1.8rem;margin-bottom:4px; }
        .range-tabs { display:flex;gap:4px;background:white;border-radius:var(--radius-md);padding:4px;border:1px solid var(--cream-dark); }
        .range-tab { padding:7px 18px;border-radius:var(--radius-sm);border:none;cursor:pointer;font-size:.82rem;font-weight:500;color:var(--text-light);background:transparent;transition:all .2s; }
        .range-tab.active { background:var(--green-deep);color:white; }
        .kpi-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:18px;margin-bottom:24px; }
        .kpi-card { background:white;border-radius:var(--radius-lg);padding:22px;border:1px solid var(--cream-dark); }
        .kpi-icon { width:44px;height:44px;border-radius:var(--radius-md);display:flex;align-items:center;justify-content:center;margin-bottom:16px; }
        .kpi-label { font-size:.75rem;text-transform:uppercase;letter-spacing:.06em;color:var(--text-light);margin-bottom:6px; }
        .kpi-value { font-family:'Playfair Display',serif;font-size:1.9rem;font-weight:700;color:var(--green-deep);margin-bottom:8px; }
        .kpi-sub { display:flex;align-items:center;gap:6px;font-size:.78rem;color:var(--text-light); }
        .trend { display:flex;align-items:center;gap:3px;font-size:.75rem;font-weight:600;padding:2px 8px;border-radius:999px; }
        .trend.up { color:var(--success);background:var(--success-soft); }
        .trend.down { color:var(--danger);background:var(--danger-soft); }
        .charts-row { display:grid;grid-template-columns:2fr 1fr;gap:20px;margin-bottom:24px; }
        .chart-card { background:white;border-radius:var(--radius-lg);padding:22px;border:1px solid var(--cream-dark); }
        .chart-card.wide { grid-column:1; }
        .chart-title { font-size:.95rem;font-weight:700;color:var(--green-deep);margin-bottom:18px; }
        .bottom-row { display:grid;grid-template-columns:repeat(3,1fr);gap:20px; }
        .table-card { background:white;border-radius:var(--radius-lg);padding:20px;border:1px solid var(--cream-dark); }
        .no-data { padding:20px 0;text-align:center;color:var(--text-light);font-size:.88rem; }
        .low-stock-row { display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--cream-dark); }
        .low-stock-row:last-child { border-bottom:none; }
        .recent-order-row { display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--cream-dark); }
        .recent-order-row:last-child { border-bottom:none; }
        @media(max-width:900px) {
          .kpi-grid { grid-template-columns:1fr 1fr; }
          .charts-row { grid-template-columns:1fr; }
          .bottom-row { grid-template-columns:1fr; }
        }
      `}</style>
    </div>
  );
}

function KPICard({ icon, label, value, sub, trend, color }) {
  const colors = { green:'#D4E8DC', blue:'#DBEAFE', gold:'#FEF3C7', purple:'#EDE9FE' };
  const fgColors = { green:'var(--green-deep)', blue:'#1D4ED8', gold:'var(--warning)', purple:'#7C3AED' };
  return (
    <div className="kpi-card">
      <div className="kpi-icon" style={{ background: colors[color] || colors.green }}>
        {React.cloneElement(icon, { size: 20, color: fgColors[color] || fgColors.green })}
      </div>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value ?? '—'}</div>
      <div className="kpi-sub">
        {sub}
        {trend !== undefined && (
          <span className={`trend ${trend >= 0 ? 'up' : 'down'}`}>
            {trend >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}
